import { useEffect, useRef } from 'react';
import type { PaymentRecord } from '@big2/shared';
import { useI18n } from '../../i18n.js';
import { useGameState } from '../../net/GameProvider.js';
import { StatusNotice } from '../StatusNotice.js';
import { usePaymentRefresh } from './usePaymentRefresh.js';
import './paymentHistory.css';

interface PaymentHistoryPageProps {
  onBack: () => void;
  onOpenProfile: () => void;
}

function paymentStatus(status: PaymentRecord['status'], isMn: boolean) {
  if (status === 'paid') return isMn ? 'Баталгаажсан' : 'Confirmed';
  if (status === 'pending') return isMn ? 'Хүлээгдэж байна' : 'Pending';
  if (status === 'review') return isMn ? 'Шалгаж байна' : 'Needs review';
  return isMn ? 'Үүсгэж байна' : 'Creating';
}

function paymentDate(value: string, isMn: boolean) {
  const date = new Date(value);
  if (isMn) return `${date.getFullYear()} оны ${date.getMonth() + 1}-р сарын ${date.getDate()}`;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function PaymentHistoryPage({ onBack, onOpenProfile }: PaymentHistoryPageProps) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const profileId = state.profile?.id;
  useEffect(() => {
    if (profileId) actionsRef.current.loadBillingHistory();
  }, [profileId]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = isMn ? 'Төлбөрийн түүх | arvangurav.com' : 'Payment history | arvangurav.com';
    return () => { document.title = previousTitle; };
  }, [isMn]);

  const { refreshPayment, refreshRequest, refreshNotice } = usePaymentRefresh();
  const history = state.billingHistory;

  return (
    <section className="payment-page" aria-labelledby="payment-page-title">
      <div className="payment-page__inner">
        <nav className="payment-page__nav" aria-label={isMn ? 'Буцах зам' : 'Breadcrumb'}>
          <a href="/" onClick={(event) => { event.preventDefault(); onBack(); }}>
            <span aria-hidden="true">←</span> {isMn ? 'Тоглоом руу буцах' : 'Back to game'}
          </a>
        </nav>

        <header className="payment-page__header">
          <div>
            <p className="payment-page__eyebrow">{isMn ? 'БҮРТГЭЛ / ТӨЛБӨР' : 'ACCOUNT / PAYMENTS'}</p>
            <h1 id="payment-page-title">{isMn ? 'Төлбөрийн түүх' : 'Payment history'}</h1>
            <p className="payment-page__intro">
              {isMn ? 'QPay төлбөрийн төлөв болон нэхэмжлэхээ эндээс шалгана уу.' : 'Review your QPay payments and invoice status.'}
            </p>
          </div>
          {state.profile && (
            <div className="payment-page__account">
              <span>{isMn ? 'ТАНЫ БҮРТГЭЛ' : 'YOUR ACCOUNT'}</span>
              <strong>{state.profile.displayName}</strong>
            </div>
          )}
        </header>

        {!state.profile && state.authToken ? (
          <p className="payment-page__loading" role="status">{isMn ? 'Бүртгэлийг шалгаж байна…' : 'Checking your account…'}</p>
        ) : !state.profile ? (
          <div className="payment-page__empty">
            <h2>{isMn ? 'Нэвтэрч төлбөрөө харна уу' : 'Sign in to view payments'}</h2>
            <p>{isMn ? 'Төлбөрийн түүх зөвхөн тухайн бүртгэлийн эзэмшигчид харагдана.' : 'Only the account owner can view this payment history.'}</p>
            <button type="button" onClick={onOpenProfile}>{isMn ? 'Нэвтрэх' : 'Sign in'}</button>
          </div>
        ) : (
          <div className="payment-page__body">
            <div className="payment-page__toolbar">
              <div>
                <h2>{isMn ? 'Нэхэмжлэхүүд' : 'Invoices'}</h2>
                {history && <span className="payment-page__count">{history.length}</span>}
              </div>
              <button type="button" onClick={() => actions.loadBillingHistory()} disabled={state.connectionStatus !== 'connected'}>
                {isMn ? 'Жагсаалт шинэчлэх' : 'Refresh list'}
              </button>
            </div>

            {history === null ? (
              <p className="payment-page__loading" role="status">{isMn ? 'Төлбөрүүдийг уншиж байна…' : 'Loading payments…'}</p>
            ) : history.length === 0 ? (
              <div className="payment-page__empty">
                <h2>{isMn ? 'Төлбөрийн бүртгэл алга' : 'No payments yet'}</h2>
                <p>{isMn ? 'VIP эрх худалдан авбал нэхэмжлэх энд харагдана.' : 'Your VIP purchases will appear here.'}</p>
                <button type="button" onClick={onOpenProfile}>{isMn ? 'VIP эрх үзэх' : 'Explore VIP'}</button>
              </div>
            ) : (
              <ol className="payment-page__list">
                {history.map((payment) => (
                  <li key={payment.id}>
                    <article className="payment-entry">
                      <div className="payment-entry__main">
                        <div>
                          <span className="payment-entry__kind">{payment.purpose === 'vip' ? (isMn ? 'VIP ГИШҮҮНЧЛЭЛ' : 'VIP MEMBERSHIP') : (isMn ? 'ЦАЙ' : 'TSAI')}</span>
                          <div className="payment-entry__amount">₮{payment.amount.toLocaleString(isMn ? 'mn-MN' : 'en-US')}</div>
                          <time dateTime={payment.paid_at ?? payment.created_at}>
                            {paymentDate(payment.paid_at ?? payment.created_at, isMn)}
                          </time>
                        </div>
                        <span className={`payment-entry__status payment-entry__status--${payment.status}`}>
                          {paymentStatus(payment.status, isMn)}
                        </span>
                      </div>
                      <div className="payment-entry__foot">
                        <div>
                          <span>{isMn ? 'Нэхэмжлэхийн дугаар' : 'Invoice reference'}</span>
                          <code>{payment.invoice_id ?? payment.id}</code>
                        </div>
                        {payment.status === 'pending' && payment.invoice_id && (
                          <button type="button" disabled={state.connectionStatus !== 'connected' || refreshRequest !== null} onClick={() => refreshPayment(payment.invoice_id!)}>
                            {isMn ? 'Төлбөр шалгах' : 'Check payment'}
                          </button>
                        )}
                      </div>
                    </article>
                  </li>
                ))}
              </ol>
            )}
            {refreshRequest && <StatusNotice tone="pending" message={isMn ? 'Төлөв шалгаж байна…' : 'Checking payment status…'} />}
            {refreshNotice && <StatusNotice {...refreshNotice} />}
            {state.connectionStatus !== 'connected' && <StatusNotice tone="error" message={isMn ? 'Сервертэй холбогдоогүй. Төлөв шалгах боломжгүй.' : 'Disconnected from server. Cannot refresh payment status.'} />}
          </div>
        )}
      </div>
    </section>
  );
}
